import React from "react";
import { Link } from "@tanstack/react-router";
import { createPortal } from "react-dom";
import { ArrowLeft, Crown, Info, X } from "lucide-react";
import { ApiError, errorMessage } from "@atlas/shared";
import { ProfileIdentity } from "../../components/ProfileIdentity";
import { findImageCredit } from "../../lib/image-credits";
import { Button } from "../../components/Button";
import { getGroupImageUploadUrl } from "../../api/groups";
import type { EquippedCosmeticItem } from "../../api/profile";
import type {
  BoardRow,
  ChallengeState,
  ChallengeSummary,
  ChallengeTargetType,
  GroupBoardTab,
  GroupMemberIdentity,
} from "../../api/groups";

// Shared bits for the group pages (spec 32 / ATL-151).

// ─── Dates ('YYYY-MM-DD', Europe/London) ──────────────────────────────────────
// Challenge dates are London calendar days. They are split, never fed to
// new Date(), so the browser's own time zone can't shift them by a day.

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export function londonToday(): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/London",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());
}

function parts(ymd: string): [number, number, number] {
  const [y, m, d] = ymd.split("-").map((n) => Number(n));
  return [y ?? 1970, m ?? 1, d ?? 1];
}

/** Whole days from a to b (b - a), both 'YYYY-MM-DD'. */
export function daysBetween(a: string, b: string): number {
  const [ay, am, ad] = parts(a);
  const [by, bm, bd] = parts(b);
  return Math.round((Date.UTC(by, bm - 1, bd) - Date.UTC(ay, am - 1, ad)) / 86_400_000);
}

export function addDays(ymd: string, n: number): string {
  const [y, m, d] = parts(ymd);
  const t = new Date(Date.UTC(y, m - 1, d + n));
  return t.toISOString().slice(0, 10);
}

/** "12 Oct" (this year) or "12 Oct 2025". */
export function fmtDay(ymd: string, withYear?: boolean): string {
  const [y, m, d] = parts(ymd);
  const showYear = withYear ?? y !== Number(londonToday().slice(0, 4));
  return `${d} ${MONTHS[m - 1] ?? ""}${showYear ? ` ${y}` : ""}`;
}

export function fmtRange(start: string, end: string): string {
  return `${fmtDay(start)} to ${fmtDay(end)}`;
}

export function fmtNumber(n: number): string {
  return n.toLocaleString("en-GB");
}

// ─── Challenges ───────────────────────────────────────────────────────────────

/** One line on where a challenge is in its life. */
export function challengeWhen(c: Pick<ChallengeSummary, "state" | "startsOn" | "endsOn">): string {
  const today = londonToday();
  const st: ChallengeState = c.state;
  if (st === "upcoming") {
    const days = daysBetween(today, c.startsOn);
    return days <= 1 ? `Starts ${days === 0 ? "today" : "tomorrow"}` : `Starts ${fmtDay(c.startsOn)} · in ${days} days`;
  }
  if (st === "active") {
    const left = daysBetween(today, c.endsOn);
    if (left <= 0) return "Last day";
    return `Ends ${fmtDay(c.endsOn)} · ${left} day${left === 1 ? "" : "s"} to go`;
  }
  if (st === "ended") return `Ended ${fmtDay(c.endsOn)} · results in the morning`;
  return `Finished ${fmtDay(c.endsOn)}`;
}

/** The unit a challenge's value counts. */
export function challengeUnit(type: ChallengeTargetType, countRepeats: boolean): string {
  if (type === "new_ground") return "squares";
  return countRepeats ? "reached" : "collected";
}

export function challengeRulesLine(c: Pick<ChallengeSummary, "target" | "countRepeats">): string {
  if (c.target.type === "new_ground") return "A square counts when it is new to you, the first time you explore it.";
  if (c.countRepeats) return "Repeat visits to hills and landmarks count. Trail sections count the first time you complete them.";
  return "Only hills, landmarks and trail sections collected for the first time during the challenge count.";
}

export function winnerNames(c: Pick<ChallengeSummary, "winners">): string {
  const names = c.winners.map((w) => w.name?.trim() || w.username || "A member");
  if (names.length <= 1) return names[0] ?? "";
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}

// ─── Boards ───────────────────────────────────────────────────────────────────

export const BOARD_UNITS: Record<GroupBoardTab, string> = {
  collected: "collected",
  score: "pts",
  new_ground: "squares",
};

// ─── Errors ───────────────────────────────────────────────────────────────────

export function isUpgradeRequired(err: unknown): boolean {
  return err instanceof ApiError && (err.status === 402 || err.upgradeRequired);
}

/** Readable copy for the group endpoints' machine codes. */
export function groupErrorMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    const code = err.serverMessage;
    if (err.upgradeRequired || err.status === 402) return "That needs Atlas Pro.";
    // 409s carry a written `message` beside the machine code.
    const written = typeof err.body?.["message"] === "string" ? (err.body["message"] as string) : null;
    if (err.status === 409 && written) return written;
    if (code === "group_full") return "This group is full, so it can't take any more members.";
    if (code === "owner_must_transfer") return "Hand ownership to another member before you leave.";
    if (code === "challenge_ended") return "That challenge has already ended.";
    if (err.status === 404) return "That group isn't available. The link may be out of date, or the group was deleted.";
  }
  return errorMessage(err, fallback);
}

// ─── Identity ─────────────────────────────────────────────────────────────────

export function memberName(m: Pick<GroupMemberIdentity, "name" | "username">): string {
  return m.name?.trim() || m.username || "Explorer";
}

/**
 * Avatar + name for a group member. Links to their explorer profile only
 * when it is public; private profiles show name and avatar and nothing else.
 */
export function MemberIdentity({
  member,
  subtitle,
  size = "sm",
}: {
  member: GroupMemberIdentity;
  subtitle?: React.ReactNode;
  size?: "sm" | "md";
}) {
  const identity = (
    <ProfileIdentity
      name={memberName(member)}
      avatarUrl={member.avatarUrl}
      // Shared EquippedItem carries no rarity; ProfileIdentity only reads it for badges.
      title={member.title as EquippedCosmeticItem | null}
      frame={member.frame as EquippedCosmeticItem | null}
      size={size}
      subtitle={subtitle}
    />
  );
  if (member.profilePublic && member.username) {
    return (
      <Link
        to="/explorers/$username"
        params={{ username: member.username }}
        style={{ textDecoration: "none", color: "inherit", minWidth: 0, display: "block" }}
      >
        {identity}
      </Link>
    );
  }
  return <div style={{ minWidth: 0 }}>{identity}</div>;
}

/** Group image, or its initials on a sunken tile. */
export function GroupImage({ name, imageUrl, size = 48 }: { name: string; imageUrl: string | null; size?: number }) {
  const initials =
    name
      .split(/\s+/)
      .map((p) => p[0])
      .filter(Boolean)
      .slice(0, 2)
      .join("")
      .toUpperCase() || "G";
  return (
    <div
      aria-hidden="true"
      style={{
        width: size,
        height: size,
        flexShrink: 0,
        borderRadius: "var(--radius-md)",
        overflow: "hidden",
        background: "var(--surface-sunken)",
        border: "1px solid var(--border-subtle)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: "var(--font-display)",
        fontWeight: 700,
        fontSize: Math.round(size * 0.36),
        color: "var(--text-secondary)",
      }}
    >
      {imageUrl ? (
        <img src={imageUrl} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
      ) : (
        initials
      )}
    </div>
  );
}

/** Gold is reserved for this: a challenge winner's mark. */
export function WinnerMark({ size = 14, title = "Challenge winner" }: { size?: number; title?: string }) {
  return (
    <Crown
      size={size}
      aria-label={title}
      style={{ color: "var(--gold-400)", flexShrink: 0 }}
      strokeWidth={2.2}
    />
  );
}

// ─── Segmented control ────────────────────────────────────────────────────────

export function Segmented<T extends string>({
  value,
  options,
  onChange,
  label,
}: {
  value: T;
  options: Array<{ value: T; label: string }>;
  onChange: (v: T) => void;
  label: string;
}) {
  return (
    <div
      role="tablist"
      aria-label={label}
      style={{
        display: "inline-flex",
        gap: 2,
        padding: 2,
        borderRadius: "var(--radius-md)",
        background: "var(--surface-sunken)",
        border: "1px solid var(--border-subtle)",
        maxWidth: "100%",
        overflowX: "auto",
      }}
    >
      {options.map((o) => {
        const active = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(o.value)}
            style={{
              padding: "5px 11px",
              borderRadius: "calc(var(--radius-md) - 2px)",
              border: "none",
              background: active ? "var(--surface-raised)" : "transparent",
              color: active ? "var(--text-primary)" : "var(--text-muted)",
              fontFamily: "var(--font-sans)",
              fontSize: 12.5,
              fontWeight: active ? 600 : 500,
              cursor: "pointer",
              whiteSpace: "nowrap",
              flexShrink: 0,
            }}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

// ─── Standings table (board + challenge) ──────────────────────────────────────

/**
 * Ranked rows. Your own row gets the accent tint; the gold crown marks
 * challenge winners only. Unranked rows (on zero) sit at the bottom.
 * Your row also says how far you are from the place above.
 */
export function Standings({
  rows,
  unit,
  winnerIds,
  emptyHint,
  total,
}: {
  rows: BoardRow[];
  unit: string;
  winnerIds?: Set<string>;
  emptyHint?: string;
  /** Collection boards: values read "121 / 214" and your row adds what's left. */
  total?: number;
}) {
  if (rows.length === 0) {
    return (
      <p style={{ fontSize: 13, color: "var(--text-muted)", margin: 0 }}>
        {emptyHint ?? "Nothing on the board yet."}
      </p>
    );
  }

  return (
    <ol style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 2 }}>
      {rows.map((row, i) => {
        const above = i > 0 ? rows[i - 1] : undefined;
        let gap: string | null = null;
        if (row.isMe && row.rank != null) {
          if (row.rank === 1) {
            const sharedTop = rows.some((r) => r !== row && r.rank === 1);
            const next = rows.find((r) => r.rank != null && r.rank > 1);
            gap = sharedTop ? "Level at the top" : next ? `${fmtNumber(row.value - next.value)} ahead` : "Out in front";
          } else if (above && above.rank != null) {
            const diff = above.value - row.value;
            gap = diff > 0 ? `${fmtNumber(diff)} to pass ${memberName(above)}` : `Level with ${memberName(above)}`;
          }
        } else if (row.isMe && row.rank == null) {
          gap = "Not on the board yet";
        }
        if (row.isMe && row.rank != null && total != null && total > 0) {
          const left = Math.max(0, total - row.value);
          const togo = left === 0 ? "all done" : `${fmtNumber(left)} to go`;
          gap = gap ? `${gap} · ${togo}` : togo;
        }
        const isWinner = winnerIds?.has(row.userId) ?? false;
        return (
          <li
            key={row.userId}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              padding: "8px 10px",
              borderRadius: "var(--radius-md)",
              background: row.isMe ? "var(--accent-soft)" : "transparent",
              border: `1px solid ${row.isMe ? "var(--border-gold)" : "transparent"}`,
              minWidth: 0,
            }}
          >
            <span
              className="tnum"
              style={{
                width: 26,
                flexShrink: 0,
                textAlign: "right",
                fontFamily: "var(--font-mono)",
                fontSize: 12,
                fontWeight: 700,
                color: row.rank == null ? "var(--text-faint)" : "var(--text-secondary)",
              }}
            >
              {row.rank ?? "–"}
            </span>
            <div style={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center", gap: 8 }}>
              <div style={{ minWidth: 0 }}>
                <MemberIdentity
                  member={row}
                  subtitle={
                    gap ? (
                      <span className="tnum" style={{ fontSize: 11.5, color: "var(--text-muted)" }}>
                        {gap}
                      </span>
                    ) : undefined
                  }
                />
              </div>
              {isWinner && <WinnerMark />}
            </div>
            <span
              className="tnum"
              style={{
                flexShrink: 0,
                fontFamily: "var(--font-display)",
                fontWeight: 700,
                fontSize: 16,
                color: row.rank == null ? "var(--text-faint)" : "var(--text-primary)",
              }}
            >
              {fmtNumber(row.value)}
              {total != null && (
                <span style={{ fontWeight: 600, fontSize: 13, color: "var(--text-muted)" }}> / {fmtNumber(total)}</span>
              )}
              {unit && (
                <span style={{ fontFamily: "var(--font-mono)", fontWeight: 500, fontSize: 10.5, color: "var(--text-muted)", marginLeft: 5 }}>
                  {unit}
                </span>
              )}
            </span>
          </li>
        );
      })}
    </ol>
  );
}

// ─── Page chrome ──────────────────────────────────────────────────────────────

export function GroupsPageFrame({ children, wide = false }: { children: React.ReactNode; wide?: boolean }) {
  return (
    <div className="r-page" style={{ padding: 28, overflow: "auto", flex: 1, display: "flex", flexDirection: "column", gap: 20 }}>
      <div
        style={{
          maxWidth: wide ? 1440 : 1080,
          width: "100%",
          margin: "0 auto",
          display: "flex",
          flexDirection: "column",
          gap: wide ? 24 : 20,
          minWidth: 0,
        }}
      >
        {children}
      </div>
    </div>
  );
}

// Card hover lift for linked cards on the group pages (render once in a <style>).
export const LIFT_CSS = `
.grp-lift{ transition: transform var(--dur-quick) var(--ease-out), border-color var(--dur-quick) var(--ease-out), box-shadow var(--dur-quick) var(--ease-out); }
.grp-lift:hover{ transform: translateY(-2px); border-color: var(--border-default) !important; box-shadow: var(--shadow-md), var(--ring-top) !important; }
.grp-lift:focus-visible{ outline: 2px solid var(--accent); outline-offset: 2px; }
@media (prefers-reduced-motion: reduce){ .grp-lift, .grp-lift:hover{ transition: none; transform: none; } }
`;

/** The plain card surface the group pages sit their sections on. */
export const CARD_STYLE: React.CSSProperties = {
  background: "var(--surface-card)",
  border: "1px solid var(--border-subtle)",
  borderRadius: "var(--radius-lg)",
  boxShadow: "var(--shadow-sm), var(--ring-top)",
  minWidth: 0,
};

/** One heading style for every section on the group pages. */
export function SectionHeading({
  title,
  hint,
  action,
}: {
  title: string;
  hint?: React.ReactNode;
  action?: React.ReactNode;
}) {
  return (
    <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 12, flexWrap: "wrap", minWidth: 0 }}>
      <div style={{ minWidth: 0, flex: "1 1 220px" }}>
        <h2 style={{ font: "var(--type-h3)", fontSize: 18, color: "var(--text-primary)", margin: 0 }}>{title}</h2>
        {hint && (
          <p style={{ fontSize: 12.5, color: "var(--text-muted)", margin: "3px 0 0", lineHeight: 1.5 }}>{hint}</p>
        )}
      </div>
      {action}
    </div>
  );
}

// ─── Cover hero ───────────────────────────────────────────────────────────────
// Photo band at the top of the group pages. The scrim is a fixed dark ink in
// every theme (it sits on photos), so everything on it uses the fixed light
// HERO_* colours rather than semantic text tokens.

export const HERO_TEXT = "#fff";
export const HERO_TEXT_SOFT = "rgba(255,255,255,.8)";
export const HERO_TEXT_FAINT = "rgba(255,255,255,.62)";

/** A frosted control that reads on any photo. */
export const HERO_CONTROL_STYLE: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 6,
  height: 34,
  padding: "0 12px",
  borderRadius: "var(--radius-pill)",
  border: "1px solid rgba(255,255,255,.2)",
  background: "rgba(9,12,16,.48)",
  backdropFilter: "blur(10px)",
  WebkitBackdropFilter: "blur(10px)",
  color: HERO_TEXT,
  fontFamily: "var(--font-sans)",
  fontSize: 13,
  fontWeight: 600,
  textDecoration: "none",
  cursor: "pointer",
  whiteSpace: "nowrap",
  flexShrink: 0,
};

/**
 * Photo credits for a hero: a small info button in the bottom row that opens
 * the credits (hover shows them too). Sits in the flow, so it never covers text.
 */
function HeroCredit({ images }: { images: string[] }) {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef<HTMLDivElement>(null);
  const credits = images.map((src) => findImageCredit(src)).filter((c): c is NonNullable<typeof c> => !!c);
  React.useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);
  if (credits.length === 0) return null;
  const summary = credits.map((c) => `${c.title} by ${c.author} (${c.license})`).join("; ");
  return (
    <div ref={ref} style={{ position: "relative", flexShrink: 0, alignSelf: "flex-end", marginLeft: "auto" }}>
      <button
        type="button"
        aria-label={credits.length === 1 ? "Photo credit" : "Photo credits"}
        aria-expanded={open}
        title={summary}
        onClick={() => setOpen((o) => !o)}
        style={{
          ...HERO_CONTROL_STYLE,
          width: 26,
          height: 26,
          padding: 0,
          color: HERO_TEXT_FAINT,
          background: "rgba(9,12,16,.35)",
        }}
      >
        <Info size={13} />
      </button>
      {open && (
        <div
          role="dialog"
          aria-label="Photo credits"
          style={{
            position: "absolute",
            right: 0,
            bottom: "calc(100% + 8px)",
            width: "min(300px, calc(100vw - 64px))",
            padding: "10px 12px",
            borderRadius: "var(--radius-md)",
            border: "1px solid rgba(255,255,255,.14)",
            background: "rgba(9,12,16,.88)",
            backdropFilter: "blur(10px)",
            WebkitBackdropFilter: "blur(10px)",
            boxShadow: "var(--shadow-lg)",
            display: "flex",
            flexDirection: "column",
            gap: 6,
            fontSize: 12,
            lineHeight: 1.45,
            color: HERO_TEXT_SOFT,
            zIndex: 3,
          }}
        >
          <span style={{ ...HERO_EYEBROW_STYLE, fontSize: 9.5, color: HERO_TEXT_FAINT }}>
            {credits.length === 1 ? "Photo" : "Photos"}
          </span>
          {credits.map((c) => (
            <a
              key={c.image}
              href={c.sourceUrl}
              target="_blank"
              rel="noreferrer"
              style={{ color: HERO_TEXT_SOFT, textDecoration: "none" }}
            >
              {c.title}, {c.author} ({c.license})
            </a>
          ))}
          <Link to="/credits" style={{ color: HERO_TEXT, fontWeight: 600, textDecoration: "none" }}>
            All image credits
          </Link>
        </div>
      )}
    </div>
  );
}

/**
 * Full-width cover band: one image, or a mosaic of up to three, or the topo
 * texture when there are none. `top` sits along the top edge (back link,
 * actions); `children` sit at the bottom left over the scrim.
 */
export function CoverHero({
  images,
  top,
  children,
  compact = false,
}: {
  images: string[];
  top?: React.ReactNode;
  children: React.ReactNode;
  /** Shorter band for the sub-pages. */
  compact?: boolean;
}) {
  const shown = images.slice(0, 3);
  const single = shown.length === 1 ? shown[0]! : null;
  return (
    <div
      style={{
        position: "relative",
        borderRadius: "var(--radius-xl)",
        border: "1px solid var(--border-default)",
        boxShadow: "var(--shadow-md)",
        minHeight: compact ? "clamp(180px, 22vw, 210px)" : "clamp(210px, 24vw, 260px)",
        // The scroll column would otherwise squash the band before scrolling.
        flexShrink: 0,
        display: "flex",
        minWidth: 0,
        background: "var(--ink-900)",
      }}
    >
      {/* Image layer: clipped on its own so popovers from the band aren't. */}
      <div aria-hidden="true" style={{ position: "absolute", inset: 0, borderRadius: "inherit", overflow: "hidden" }}>
        {shown.length === 0 ? (
          <>
            <div className="atlas-topo" style={{ position: "absolute", inset: 0 }} />
            <div style={{ position: "absolute", inset: 0, background: "rgba(9,12,16,.55)" }} />
          </>
        ) : single ? (
          <img src={single} alt="" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }} />
        ) : (
          <div style={{ position: "absolute", inset: 0, display: "grid", gridTemplateColumns: `repeat(${shown.length}, minmax(0, 1fr))`, gap: 2, background: "rgba(9,12,16,.9)" }}>
            {shown.map((src) => (
              <img key={src} src={src} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
            ))}
          </div>
        )}
        <div
          style={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(180deg, rgba(9,12,16,.45) 0%, rgba(9,12,16,.12) 30%, rgba(9,12,16,.5) 62%, rgba(9,12,16,.88) 100%), linear-gradient(90deg, rgba(9,12,16,.55) 0%, rgba(9,12,16,0) 65%)",
          }}
        />
      </div>

      <div
        style={{
          position: "relative",
          zIndex: 2,
          flex: 1,
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          gap: 20,
          padding: "clamp(16px, 2.4vw, 24px) clamp(16px, 2.4vw, 28px)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap", minWidth: 0 }}>
          {top}
        </div>
        <div style={{ display: "flex", alignItems: "flex-end", gap: 16, minWidth: 0 }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 6, minWidth: 0, flex: "0 1 760px" }}>{children}</div>
          <HeroCredit images={shown} />
        </div>
      </div>
    </div>
  );
}

/** Back link contents for the top of a cover hero (wrap in a Link with HERO_BACK_STYLE). */
export const HERO_BACK_STYLE: React.CSSProperties = {
  ...HERO_CONTROL_STYLE,
  height: 30,
  padding: "0 11px 0 9px",
  fontSize: 12.5,
  maxWidth: "100%",
  minWidth: 0,
  flexShrink: 1,
};

export function HeroBackLabel({ label }: { label: string }) {
  return (
    <>
      <ArrowLeft size={14} style={{ flexShrink: 0 }} />
      <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{label}</span>
    </>
  );
}

export const HERO_EYEBROW_STYLE: React.CSSProperties = {
  fontFamily: "var(--font-mono)",
  fontSize: 10.5,
  letterSpacing: ".14em",
  textTransform: "uppercase",
  color: HERO_TEXT_SOFT,
  margin: 0,
};

export const HERO_TITLE_STYLE: React.CSSProperties = {
  font: "var(--type-display)",
  fontSize: "clamp(28px, 3.6vw, 44px)",
  letterSpacing: "-.02em",
  lineHeight: 1.1,
  color: HERO_TEXT,
  margin: 0,
  textShadow: "0 1px 12px rgba(0,0,0,.35)",
  overflowWrap: "anywhere",
};

/** Small round avatar (photo or initials), for stacks and timelines. */
export function MiniAvatar({
  member,
  size = 26,
  ring,
}: {
  member: Pick<GroupMemberIdentity, "name" | "username" | "avatarUrl">;
  size?: number;
  /** Border colour; defaults to the card surface so stacks read as separate discs. */
  ring?: string;
}) {
  const name = memberName(member);
  const initials =
    name
      .split(/\s+/)
      .map((p) => p[0])
      .filter(Boolean)
      .slice(0, 2)
      .join("")
      .toUpperCase() || "?";
  return (
    <span
      aria-hidden="true"
      style={{
        width: size,
        height: size,
        flexShrink: 0,
        borderRadius: "50%",
        overflow: "hidden",
        border: `2px solid ${ring ?? "var(--surface-card)"}`,
        background: "var(--surface-raised)",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: "var(--font-display)",
        fontWeight: 700,
        fontSize: Math.max(9, Math.round(size * 0.4)),
        letterSpacing: "-.02em",
        color: "var(--text-accent)",
        boxSizing: "border-box",
      }}
    >
      {member.avatarUrl ? (
        <img src={member.avatarUrl} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
      ) : (
        initials
      )}
    </span>
  );
}

export const INPUT_STYLE: React.CSSProperties = {
  width: "100%",
  height: "var(--control-md)",
  padding: "0 12px",
  background: "var(--surface-sunken)",
  border: "1px solid var(--border-default)",
  borderRadius: "var(--radius-md)",
  color: "var(--text-primary)",
  fontSize: 14,
  fontFamily: "var(--font-sans)",
  outline: "none",
  boxSizing: "border-box",
  minWidth: 0,
};

export const LABEL_STYLE: React.CSSProperties = {
  fontSize: 13,
  fontWeight: 600,
  color: "var(--text-secondary)",
};

export function ErrorLine({ children }: { children: React.ReactNode }) {
  return <p style={{ fontSize: 12.5, color: "var(--status-danger)", margin: 0 }}>{children}</p>;
}

// ─── Group image picker ───────────────────────────────────────────────────────
// Presigned PUT via POST /groups/image-upload-url; the API accepts only the
// returned publicUrl (or the group's current image) as imageUrl.

const IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"];
const MAX_IMAGE_BYTES = 5 * 1024 * 1024;

export function GroupImagePicker({
  name,
  value,
  onChange,
  onBusy,
}: {
  name: string;
  value: string | null;
  onChange: (url: string | null) => void;
  onBusy?: (busy: boolean) => void;
}) {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  async function handle(file: File) {
    setError(null);
    if (!IMAGE_TYPES.includes(file.type)) {
      setError("Images must be JPEG, PNG or WebP.");
      return;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      setError("That image is over 5 MB.");
      return;
    }
    setBusy(true);
    onBusy?.(true);
    try {
      const { uploadUrl, publicUrl } = await getGroupImageUploadUrl(file.type);
      const res = await fetch(uploadUrl, { method: "PUT", body: file, headers: { "Content-Type": file.type } });
      if (!res.ok) throw new Error("upload failed");
      onChange(publicUrl);
    } catch (err) {
      setError(errorMessage(err, "Couldn't upload the image. Try again."));
    } finally {
      setBusy(false);
      onBusy?.(false);
    }
  }

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
      <GroupImage name={name || "Group"} imageUrl={value} size={52} />
      <input
        ref={inputRef}
        type="file"
        accept={IMAGE_TYPES.join(",")}
        style={{ display: "none" }}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) void handle(f);
          e.target.value = "";
        }}
      />
      <Button variant="ghost" size="sm" disabled={busy} onClick={() => inputRef.current?.click()}>
        {busy ? "Uploading…" : value ? "Change image" : "Add image"}
      </Button>
      {value && !busy && (
        <Button variant="ghost" size="sm" onClick={() => onChange(null)}>
          Remove
        </Button>
      )}
      {error && <ErrorLine>{error}</ErrorLine>}
    </div>
  );
}

// ─── Switch ───────────────────────────────────────────────────────────────────

/** On/off toggle (track + knob). Pass `labelledBy` or `label` for its name. */
export function Switch({
  checked,
  onChange,
  label,
  labelledBy,
  describedBy,
  id,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label?: string;
  labelledBy?: string;
  describedBy?: string;
  id?: string;
}) {
  return (
    <button
      id={id}
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      aria-labelledby={labelledBy}
      aria-describedby={describedBy}
      onClick={() => onChange(!checked)}
      style={{
        position: "relative",
        width: 38,
        height: 22,
        flexShrink: 0,
        padding: 0,
        borderRadius: "var(--radius-pill)",
        border: `1px solid ${checked ? "var(--accent)" : "var(--border-strong)"}`,
        background: checked ? "var(--accent)" : "var(--surface-sunken)",
        cursor: "pointer",
        transition: "background var(--dur-quick) var(--ease-out), border-color var(--dur-quick) var(--ease-out)",
      }}
    >
      <span
        aria-hidden="true"
        style={{
          position: "absolute",
          top: 2,
          left: checked ? 18 : 2,
          width: 16,
          height: 16,
          borderRadius: "50%",
          background: checked ? "var(--ink-950)" : "var(--text-secondary)",
          boxShadow: "0 1px 2px rgba(0,0,0,.35)",
          transition: "left var(--dur-quick) var(--ease-out)",
        }}
      />
    </button>
  );
}

// ─── Dialog ───────────────────────────────────────────────────────────────────

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Centred modal: backdrop and Escape close it, Tab stays inside, focus goes
 * back to whatever opened it. The body scrolls when the content is tall.
 */
export function Dialog({
  title,
  onClose,
  children,
  maxWidth = 560,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
  maxWidth?: number;
}) {
  const panelRef = React.useRef<HTMLDivElement>(null);
  const titleId = React.useId();
  const closeRef = React.useRef(onClose);
  closeRef.current = onClose;

  React.useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    const panel = panelRef.current;
    // Focus the first field (the form's autoFocus may already have done it).
    if (panel && !panel.contains(document.activeElement)) {
      const first = panel.querySelector<HTMLElement>("input, select, textarea") ?? panel.querySelector<HTMLElement>(FOCUSABLE);
      first?.focus();
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.stopPropagation();
        closeRef.current();
        return;
      }
      if (e.key !== "Tab" || !panelRef.current) return;
      const nodes = [...panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE)].filter((n) => n.offsetParent !== null);
      if (nodes.length === 0) return;
      const first = nodes[0]!;
      const last = nodes[nodes.length - 1]!;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      opener?.focus?.();
    };
  }, []);

  return createPortal(
    <div
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1000,
        background: "rgba(0,0,0,.6)",
        backdropFilter: "blur(4px)",
        WebkitBackdropFilter: "blur(4px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        style={{
          width: "100%",
          maxWidth,
          maxHeight: "calc(100dvh - 32px)",
          display: "flex",
          flexDirection: "column",
          background: "var(--surface-card)",
          border: "1px solid var(--border-strong)",
          borderRadius: "var(--radius-xl)",
          boxShadow: "var(--shadow-xl)",
          minWidth: 0,
          overflow: "hidden",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: "16px 20px", borderBottom: "1px solid var(--border-subtle)", flexShrink: 0 }}>
          <h2 id={titleId} style={{ font: "var(--type-h3)", fontSize: 18, color: "var(--text-primary)", margin: 0 }}>
            {title}
          </h2>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            style={{ background: "none", border: "none", padding: 4, cursor: "pointer", color: "var(--text-muted)", display: "inline-flex" }}
          >
            <X size={18} />
          </button>
        </div>
        <div style={{ padding: 20, overflowY: "auto", minHeight: 0 }}>{children}</div>
      </div>
    </div>,
    document.body,
  );
}
